/**
 * Home — role-aware pilot landing.
 *
 * PILOT SCOPE: the earlier G1 marketplace home (earnings hero from
 * /harvests/earnings, harvest-sourced "crops on sale" listings, harvest cards)
 * was cut along with independent harvests and harvest-sourced listings. This
 * page is now a light, role-aware launcher over surfaces that exist in the
 * pilot:
 *   - farmer / group_lead: greeting + cooperative chip, a Rewards balance tile
 *     (GET /incentives/me/balance), and shortcuts to My contributions, My farm,
 *     Market prices, Buyer demands, My orders and Notifications.
 *   - coop_admin / admin: shortcuts to Dashboard, Farmers, Batches, Orders
 *     (buyer-demand inbox), My listings and Notifications.
 *   - buyer: shortcuts to Marketplace, Post a demand, My offers, My orders,
 *     Watchlist and Notifications.
 *
 * The index redirect still lands farmers here, coop admins on /dashboard and
 * buyers on /marketplace; /home simply renders sensibly for any role that
 * reaches it.
 *
 * Resilience: every query fails OPEN — a failed /me/profile falls back to a
 * plain "Welcome back"; a failed /me/farm-profile (404 for non-farmers) renders
 * no chip; a failed balance renders "Unavailable". Nothing gates the shortcuts.
 *
 * Cache note: ['me-profile'], ['me-farm-profile'] and ['incentives-balance']
 * are the same keys the Profile / My farm / Rewards screens use.
 */
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Icon,
  Stack,
  Text,
  mdSysColor,
  useColorMode,
} from '@fse/ui';
import { Link, type LinkProps } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FseApiError, readCurrentRole } from '../api/client';
import { meControllerGetFarmProfile, meControllerGetProfile } from '../api/generated/me/me';
import type { MyFarmProfileDto } from '../api/generated/models';
import { getIncentiveBalance } from '../api/incentives';

/** A quick-launch shortcut. `to` is typed against the registered router so a
 *  removed route fails typecheck instead of becoming a dead link. */
interface NavCardDef {
  to: NonNullable<LinkProps['to']>;
  titleKey: string;
  descriptionKey: string;
  descriptionDefault: string;
  icon: string;
}

const FARMER_CARDS: readonly NavCardDef[] = [
  {
    to: '/my-contributions',
    titleKey: 'home.card.myContributions.title',
    descriptionKey: 'home.card.myContributions.description',
    descriptionDefault: "See the produce you've contributed to cooperative batches.",
    icon: 'inventory_2',
  },
  {
    to: '/my-farm',
    titleKey: 'home.card.myFarm.title',
    descriptionKey: 'home.card.myFarm.description',
    descriptionDefault: 'View and update your tree count, capacity and GPS.',
    icon: 'agriculture',
  },
  {
    to: '/market',
    titleKey: 'home.card.market.title',
    descriptionKey: 'home.card.market.description',
    descriptionDefault: "Today's indicative buyer prices",
    icon: 'storefront',
  },
  {
    to: '/demands',
    titleKey: 'nav.buyerDemands',
    descriptionKey: 'home.card.buyerDemands.description',
    descriptionDefault: 'What buyers are looking for right now',
    icon: 'campaign',
  },
  {
    to: '/orders',
    titleKey: 'nav.myOrders',
    descriptionKey: 'home.card.myOrders.description',
    descriptionDefault: 'Track your orders, deliveries and settlement',
    icon: 'receipt_long',
  },
  {
    to: '/notifications',
    titleKey: 'home.card.notifications.title',
    descriptionKey: 'home.card.notifications.description',
    descriptionDefault: 'Updates about your account',
    icon: 'notifications',
  },
];

const COOP_ADMIN_CARDS: readonly NavCardDef[] = [
  {
    to: '/dashboard',
    titleKey: 'nav.dashboard',
    descriptionKey: 'home.card.dashboard.description',
    descriptionDefault: 'Your cooperative at a glance',
    icon: 'dashboard',
  },
  {
    to: '/farmers',
    titleKey: 'nav.farmers',
    descriptionKey: 'home.card.farmers.description',
    descriptionDefault: 'Your cooperative member roster',
    icon: 'group',
  },
  {
    to: '/batches',
    titleKey: 'nav.batches',
    descriptionKey: 'home.card.batches.description',
    descriptionDefault: 'Open batches and record contributions',
    icon: 'inventory_2',
  },
  {
    to: '/demands',
    titleKey: 'nav.demands',
    descriptionKey: 'home.card.demands.description',
    descriptionDefault: 'Incoming buyer demand for your produce',
    icon: 'campaign',
  },
  {
    to: '/my-listings',
    titleKey: 'nav.myListings',
    descriptionKey: 'home.card.myListings.description',
    descriptionDefault: 'Batches you have listed on the marketplace',
    icon: 'sell',
  },
  {
    to: '/notifications',
    titleKey: 'home.card.notifications.title',
    descriptionKey: 'home.card.notifications.description',
    descriptionDefault: 'Updates about your account',
    icon: 'notifications',
  },
];

const BUYER_CARDS: readonly NavCardDef[] = [
  {
    to: '/marketplace',
    titleKey: 'nav.marketplace',
    descriptionKey: 'home.card.marketplace.description',
    descriptionDefault: 'Browse produce batches for sale',
    icon: 'storefront',
  },
  {
    to: '/create-demand',
    titleKey: 'nav.createDemand',
    descriptionKey: 'home.card.createDemand.description',
    descriptionDefault: 'Tell cooperatives what you want to buy',
    icon: 'campaign',
  },
  {
    to: '/my-offers',
    titleKey: 'nav.myOffers',
    descriptionKey: 'home.card.myOffers.description',
    descriptionDefault: 'Offers you have made on listings',
    icon: 'sell',
  },
  {
    to: '/orders',
    titleKey: 'nav.myOrders',
    descriptionKey: 'home.card.myOrders.description',
    descriptionDefault: 'Track your orders, deliveries and settlement',
    icon: 'receipt_long',
  },
  {
    to: '/watchlist',
    titleKey: 'nav.watchlist',
    descriptionKey: 'home.card.watchlist.description',
    descriptionDefault: 'Listings you are keeping an eye on',
    icon: 'star',
  },
  {
    to: '/notifications',
    titleKey: 'home.card.notifications.title',
    descriptionKey: 'home.card.notifications.description',
    descriptionDefault: 'Updates about your account',
    icon: 'notifications',
  },
];

export function HomeScreen() {
  const { t } = useTranslation();
  const role = readCurrentRole();
  const isFarmerLike = role === 'farmer' || role === 'group_lead';
  const isBuyer = role === 'buyer';

  // Greeting name — best-effort, never blocks the page.
  const profileQuery = useQuery({
    queryKey: ['me-profile'],
    queryFn: ({ signal }) => meControllerGetProfile(signal),
  });

  // Cooperative chip — farmer-like only, fail-open (404 / network → no chip).
  const farmProfileQuery = useQuery<MyFarmProfileDto, FseApiError>({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    retry: false,
    enabled: isFarmerLike,
  });

  // Rewards balance — farmer-like only; shares the Rewards screen's cache key.
  const balanceQuery = useQuery({
    queryKey: ['incentives-balance'],
    queryFn: ({ signal }) => getIncentiveBalance(signal),
    retry: false,
    enabled: isFarmerLike,
  });

  const cards = isFarmerLike ? FARMER_CARDS : isBuyer ? BUYER_CARDS : COOP_ADMIN_CARDS;

  const displayName = profileQuery.data?.displayName;
  const greeting = displayName
    ? t('home.greeting.named', { name: displayName })
    : t('home.greeting.plain');
  const coopName = farmProfileQuery.isSuccess ? farmProfileQuery.data.cooperativeName : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h1">
          {greeting}
        </Text>
        {coopName !== null ? (
          <Box sx={{ mt: 1 }}>
            <Chip
              kind="assist"
              color="success"
              leadingIcon="groups"
              label={t('home.membership.member', { name: coopName })}
            />
          </Box>
        ) : null}
      </Box>

      {isFarmerLike ? (
        <RewardsTile
          balance={balanceQuery.data?.balance}
          isPending={balanceQuery.isPending}
          isError={balanceQuery.isError}
        />
      ) : null}

      {/* Quick-launch grid — wraps to as many columns as the viewport allows. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {cards.map((card) => (
          <NavCard
            key={card.to}
            to={card.to}
            icon={card.icon}
            title={t(card.titleKey)}
            description={t(card.descriptionKey, card.descriptionDefault)}
          />
        ))}
      </Box>
    </Stack>
  );
}

/**
 * Rewards balance tile — tappable (links to /incentives). Tinted with the
 * primary-container role so it reads in light and dark. Points are plain
 * integers (no money formatting).
 */
function RewardsTile({
  balance,
  isPending,
  isError,
}: {
  balance: number | undefined;
  isPending: boolean;
  isError: boolean;
}) {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <Link to="/incentives" style={{ textDecoration: 'none', display: 'block' }}>
      <Box
        sx={{
          bgcolor: sys.primaryContainer,
          color: sys.onPrimaryContainer,
          borderRadius: 3,
          p: 2.5,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          transition: 'box-shadow 200ms ease-out',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text variant="label-medium" color="inherit">
            {t('incentives.balance.label')}
          </Text>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <Text variant="label-medium" color="inherit">
              {t('nav.incentives')}
            </Text>
            <Icon name="arrow_forward" size={16} color="inherit" />
          </Box>
        </Box>
        {isPending ? (
          <Box sx={{ py: 0.5 }}>
            <CircularProgress size={28} color="inherit" />
          </Box>
        ) : (
          <Text variant="headline-medium" as="span" color="inherit">
            {isError || balance === undefined
              ? t('incentives.balance.unavailable')
              : t('incentives.balance.points', { count: balance })}
          </Text>
        )}
      </Box>
    </Link>
  );
}

/**
 * Tappable navigation card — a Link-wrapped Card with a leading icon, bold
 * title + muted description. The 260px flex basis lays cards out in a
 * responsive grid without a Grid component.
 */
function NavCard({
  to,
  icon,
  title,
  description,
}: {
  to: NavCardDef['to'];
  icon: string;
  title: string;
  description: string;
}) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <Link to={to} style={{ textDecoration: 'none', flex: '1 1 260px', minWidth: 260 }}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'box-shadow 200ms ease-out, border-color 200ms ease-out',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardContent>
          <Stack spacing={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: sys.secondaryContainer,
                color: sys.onSecondaryContainer,
              }}
            >
              <Icon name={icon} size={24} color="inherit" />
            </Box>
            <Text variant="title-medium">{title}</Text>
            <Text variant="body-medium" color="text.secondary">
              {description}
            </Text>
          </Stack>
        </CardContent>
      </Card>
    </Link>
  );
}
